/** `sections/blog-carousel.liquid` — `<blog-carousel>`: horizontal blog-post rail
 *  with ScrollBooster-powered drag/trackpad scrolling and card-by-card arrow
 *  navigation (mirrors product-carousel behavior).
 */

import ScrollBooster from 'scrollbooster'
import { CarouselPagination } from './carousel-pagination.js'

const VIEWPORT_SELECTOR = '[data-blog-carousel-viewport]'
const TRACK_SELECTOR = '[data-blog-carousel-track]'
const CARD_SELECTOR = '[data-blog-carousel-card]'
const PREV_SELECTOR = '[data-blog-carousel-prev]'
const NEXT_SELECTOR = '[data-blog-carousel-next]'
const PAGINATION_SELECTOR = '[data-blog-carousel-pagination]'
const GRAB_CLASS = 'is-grabbing'

class BlogCarousel extends HTMLElement {
  #abort
  #booster
  #viewport
  #track
  #prevBtn
  #nextBtn
  #pagination
  #resizeObserver

  connectedCallback() {
    this.#viewport = this.querySelector(VIEWPORT_SELECTOR)
    this.#track = this.querySelector(TRACK_SELECTOR)
    this.#prevBtn = this.querySelector(PREV_SELECTOR)
    this.#nextBtn = this.querySelector(NEXT_SELECTOR)
    if (!this.#viewport || !this.#track) return

    this.#abort = new AbortController()
    const { signal } = this.#abort

    // Autoplay delay (ms) is set per section via `data-slideshow-timeout`; fall
    // back to CarouselPagination's default when the attribute is missing/invalid.
    const autoplay = Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || true

    this.#pagination = new CarouselPagination(this.querySelector(PAGINATION_SELECTOR), {
      signal,
      onSelect: (x) => this.#booster?.scrollTo({ x }),
      autoplay,
      pauseOn: this,
    })

    this.#booster = new ScrollBooster({
      viewport: this.#viewport,
      content: this.#track,
      scrollMode: 'transform',
      direction: 'horizontal',
      pointerMode: 'touch',
      emulateScroll: true,
      preventDefaultOnEmulateScroll: 'horizontal',
      lockScrollOnDragDirection: 'horizontal',
      bounce: true,
      bounceForce: 0.1,
      textSelection: true,
      onPointerDown: () => this.#viewport.classList.add(GRAB_CLASS),
      onPointerUp: () => this.#viewport.classList.remove(GRAB_CLASS),
      onUpdate: (state) => this.#syncArrowState(state),
    })

    this.#prevBtn?.addEventListener('click', this.#onPrev, { signal })
    this.#nextBtn?.addEventListener('click', this.#onNext, { signal })

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#booster?.updateMetrics()
        this.#syncArrowState()
      })
      this.#resizeObserver.observe(this.#viewport)
      this.#resizeObserver.observe(this.#track)
    }

    this.#syncArrowState()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#resizeObserver?.disconnect()
    this.#pagination?.destroy()
    this.#booster?.destroy()
    this.#booster = undefined
  }

  #onPrev = () => {
    this.#scrollByCards(-1)
  }

  #onNext = () => {
    this.#scrollByCards(1)
  }

  #scrollByCards(direction) {
    if (!this.#booster) return
    const step = this.#cardStep()
    const current = this.#booster.getState().position.x
    const max = this.#maxScroll()
    const target = Math.max(0, Math.min(max, current + direction * step))
    this.#booster.scrollTo({ x: target })
  }

  #cardStep() {
    const allCards = this.#track?.querySelectorAll(CARD_SELECTOR)
    if (!allCards || allCards.length === 0) return this.#viewport.clientWidth
    // Ignore CA fallback cards hidden via `display: none` (offsetParent is null)
    // so the per-card step is measured from cards that actually take up space.
    const cards = Array.from(allCards).filter((card) => card.offsetParent !== null)
    if (cards.length === 0) return this.#viewport.clientWidth
    const first = cards[0]
    const second = cards[1]
    if (second) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left
    }
    const style = globalThis.getComputedStyle(this.#track)
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0
    return first.getBoundingClientRect().width + gap
  }

  #maxScroll() {
    if (!this.#viewport || !this.#track) return 0
    return Math.max(0, this.#track.scrollWidth - this.#viewport.clientWidth)
  }

  #syncArrowState(state) {
    if (!this.#booster) return
    const current = (state ?? this.#booster.getState()).position.x
    const max = this.#maxScroll()
    const atStart = current <= 1
    const atEnd = current >= max - 1
    const hasOverflow = max > 1

    if (this.#prevBtn) {
      this.#prevBtn.disabled = !hasOverflow || atStart
    }
    if (this.#nextBtn) {
      this.#nextBtn.disabled = !hasOverflow || atEnd
    }

    this.#pagination?.update({ current, max, step: this.#cardStep() })
  }
}

if (!customElements.get('blog-carousel')) {
  customElements.define('blog-carousel', BlogCarousel)
}
